import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CheckCircle2, XCircle } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button, SectionLoader } from '@/components/atoms'
import { organizationService } from '@/services/organizationService'
import { clearPendingInviteToken, markOrgInviteAccepted } from '@/utils/pendingInvite'

const AcceptInviteScreen = () => {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const [orgId, setOrgId] = useState<string | null>(null)

  // Accepting is single-use and destructive on the server. React StrictMode
  // double-invokes effects in development, and two concurrent POSTs of the same
  // token race each other - one wins with a 200, the other loses with a generic
  // 500 ("An unexpected error occurred during authentication") that masks the
  // real cause. The token already sent is recorded on a ref, which survives the
  // StrictMode remount, so the request fires exactly once.
  const redeemedTokenRef = useRef<string | null>(null)
  // Kept separate from the guard above: a StrictMode remount tears the effect
  // down and back up, so an `active` flag scoped to the *effect* would be false
  // by the time the one in-flight request resolves, and the result would be
  // dropped forever - leaving the screen stuck on its loader.
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (redeemedTokenRef.current === token) return
    redeemedTokenRef.current = token

    setStatus('loading')
    organizationService
      .acceptInvitation(token)
      .then(result => {
        // Terminal state: stop replaying this token after auth redirects, and
        // record the join so the signup account-type gate lets them through.
        clearPendingInviteToken()
        markOrgInviteAccepted()
        if (!mountedRef.current) return
        setStatus('success')
        setOrgId(result.organization.id)
        setMessage(`You have joined ${result.organization.name}.`)
      })
      .catch(err => {
        // Terminal state too - a bad/expired token must not trap the user in a
        // redirect loop between /login and /invite.
        clearPendingInviteToken()
        if (!mountedRef.current) return
        setStatus('error')
        setMessage(err?.message || 'This invitation could not be accepted.')
      })
  }, [token])

  if (status === 'loading') return <SectionLoader />

  return (
    <div className="max-w-md mx-auto p-4 md:p-6">
      <Card className="p-8 text-center">
        {status === 'success' ? (
          <>
            <CheckCircle2 size={40} className="text-green-500 mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-foreground mb-2">Invitation accepted</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button
              variant="primary"
              onClick={() => navigate(orgId ? `/organizations/${orgId}` : '/organizations')}
            >
              Go to organization
            </Button>
          </>
        ) : (
          <>
            <XCircle size={40} className="text-red-500 mx-auto mb-4" />
            <h1 className="text-lg font-semibold text-foreground mb-2">Unable to accept</h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button variant="outline" onClick={() => navigate('/organizations')}>
              Go to organizations
            </Button>
          </>
        )}
      </Card>
    </div>
  )
}

export default AcceptInviteScreen
